<template>
  <div class="spotlight-demo-page">
    <!-- ========== 页面标题区 ========== -->
    <header class="page-header" data-step="1" data-intro="这是页面标题区域，展示当前模块的名称和简介。">
      <div class="header-content">
        <h1 class="page-title">
          <i class="el-icon-s-opportunity"></i>
          聚光高亮新手引导
        </h1>
        <p class="page-subtitle">基于 intro.js 的功能介绍 / 新手教程 / 步骤引导</p>
      </div>
      <div class="header-actions">
        <el-button type="primary" icon="el-icon-video-play" @click="startFullGuide">
          开始完整引导
        </el-button>
        <el-button type="success" icon="el-icon-refresh" @click="resetGuide">
          重置状态
        </el-button>
      </div>
    </header>

    <!-- ========== 功能卡片区 ========== -->
    <section class="feature-section" data-step="2" data-intro="这里展示了聚光高亮的核心能力，每个卡片代表一个功能特性。">
      <h2 class="section-title">核心能力</h2>
      <div class="feature-grid">
        <div class="feature-card" v-for="(feature, index) in features" :key="index"
             :data-step="index + 3"
             :data-intro="feature.intro">
          <div class="feature-icon" :style="{ background: feature.bg }">
            <i :class="feature.icon"></i>
          </div>
          <h3 class="feature-name">{{ feature.name }}</h3>
          <p class="feature-desc">{{ feature.desc }}</p>
        </div>
      </div>
    </section>

    <!-- ========== 演示场景区 ========== -->
    <section class="demo-scenarios">
      <h2 class="section-title">演示场景</h2>

      <!-- 场景 1：表单引导 -->
      <div class="scenario-card" id="scenario-form">
        <div class="scenario-header">
          <h3 class="scenario-title">场景 1：表单填写引导</h3>
          <el-button type="primary" size="small" @click="startFormGuide">开始引导</el-button>
        </div>
        <div class="scenario-content">
          <el-form :model="formData" label-width="100px" class="demo-form">
            <el-form-item label="用户名" data-intro="请输入您的用户名，3-20 个字符">
              <el-input v-model="formData.username" placeholder="请输入用户名" />
            </el-form-item>
            <el-form-item label="邮箱" data-intro="请输入有效的邮箱地址">
              <el-input v-model="formData.email" placeholder="请输入邮箱" />
            </el-form-item>
            <el-form-item label="角色" data-intro="选择您的角色类型">
              <el-select v-model="formData.role" placeholder="请选择角色" style="width: 100%">
                <el-option label="管理员" value="admin" />
                <el-option label="编辑" value="editor" />
                <el-option label="访客" value="guest" />
              </el-select>
            </el-form-item>
            <el-form-item label="简介" data-intro="简单介绍一下自己">
              <el-input v-model="formData.bio" type="textarea" :rows="3" placeholder="请输入个人简介" />
            </el-form-item>
            <el-form-item>
              <el-button type="primary">提交</el-button>
              <el-button>取消</el-button>
            </el-form-item>
          </el-form>
        </div>
      </div>

      <!-- 场景 2：列表操作引导 -->
      <div class="scenario-card" id="scenario-table">
        <div class="scenario-header">
          <h3 class="scenario-title">场景 2：数据列表操作引导</h3>
          <el-button type="primary" size="small" @click="startTableGuide">开始引导</el-button>
        </div>
        <div class="scenario-content">
          <el-table :data="tableData" border style="width: 100%">
            <el-table-column prop="id" label="ID" width="60" />
            <el-table-column prop="name" label="名称" width="120" />
            <el-table-column prop="status" label="状态" width="100">
              <template slot-scope="scope">
                <el-tag :type="scope.row.status === '启用' ? 'success' : 'info'" size="small">
                  {{ scope.row.status }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="date" label="日期" width="120" />
            <el-table-column prop="desc" label="描述" />
            <el-table-column label="操作" width="180">
              <template slot-scope="scope">
                <el-button size="mini" type="primary">编辑</el-button>
                <el-button size="mini" type="danger">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <!-- 场景 3：步骤流程引导 -->
      <div class="scenario-card" id="scenario-steps">
        <div class="scenario-header">
          <h3 class="scenario-title">场景 3：步骤流程引导</h3>
          <el-button type="primary" size="small" @click="startStepsGuide">开始引导</el-button>
        </div>
        <div class="scenario-content">
          <el-steps :active="stepActive" finish-status="success" align-center>
            <el-step title="基本信息" description="填写基础资料" />
            <el-step title="权限配置" description="设置访问权限" />
            <el-step title="确认提交" description="核对并提交" />
            <el-step title="完成" description="操作成功" />
          </el-steps>
          <div class="step-actions">
            <el-button type="primary" @click="stepActive++" v-if="stepActive < 4">下一步</el-button>
            <el-button @click="stepActive--" v-if="stepActive > 1">上一步</el-button>
            <el-button @click="stepActive = 1" v-if="stepActive >= 4">重新开始</el-button>
          </div>
        </div>
      </div>
    </section>

    <!-- ========== 配置说明区 ========== -->
    <section class="config-section" id="config-section">
      <h2 class="section-title">配置选项</h2>
      <div class="config-grid">
        <div class="config-item" v-for="(config, index) in configs" :key="index">
          <code class="config-name">{{ config.name }}</code>
          <span class="config-type">{{ config.type }}</span>
          <p class="config-desc">{{ config.desc }}</p>
        </div>
      </div>
    </section>

    <!-- ========== 代码示例区 ========== -->
    <section class="code-section" id="code-section">
      <h2 class="section-title">使用示例</h2>
      <div class="code-card">
        <div class="code-header">
          <span class="code-label">SpotlightGuide.vue — 组件调用</span>
          <el-button size="mini" type="primary" @click="copyCode(usageCode)">复制代码</el-button>
        </div>
        <pre class="code-block"><code>{{ usageCode }}</code></pre>
      </div>
    </section>

    <!-- ========== SpotlightGuide 组件实例 ========== -->
    <SpotlightGuide ref="spotlight" />

    <!-- ========== 引导提示气泡 ========== -->
    <div class="guide-toast" v-if="toastVisible">
      <i class="el-icon-info-circle"></i>
      <span>{{ toastMessage }}</span>
    </div>
  </div>
</template>

<script>
import SpotlightGuide from '@/components/SpotlightGuide.vue'

export default {
  name: 'SpotlightDemo',
  components: { SpotlightGuide },
  data() {
    return {
      // 功能特性列表
      features: [
        {
          name: '聚光高亮',
          desc: '通过 box-shadow 超大阴影实现压暗遮罩，目标元素高亮突出',
          icon: 'el-icon-aim',
          bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          intro: '聚光高亮：通过超大 box-shadow 实现遮罩压暗，目标区域透明高亮'
        },
        {
          name: '步骤导航',
          desc: '支持上一步/下一步/跳过/完成，键盘方向键和 ESC 控制',
          icon: 'el-icon-d-arrow-right',
          bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          intro: '步骤导航：支持按钮切换、键盘方向键、ESC 退出等多种交互方式'
        },
        {
          name: '位置自适应',
          desc: 'tooltip 位置支持 top/bottom/left/right/auto，自动适配视口',
          icon: 'el-icon-position',
          bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          intro: '位置自适应：tooltip 气泡自动选择最佳显示位置，避免溢出视口'
        },
        {
          name: '防误触',
          desc: '遮罩外点击可配置为忽略或退出，避免用户误操作',
          icon: 'el-icon-lock',
          bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          intro: '防误触：通过 exitOnOverlayClick 配置控制遮罩外点击行为'
        },
        {
          name: '事件回调',
          desc: '提供 onComplete / onExit / onChange / onBeforeChange 回调',
          icon: 'el-icon-connection',
          bg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
          intro: '事件回调：丰富的生命周期钩子，可对接业务逻辑和埋点统计'
        },
        {
          name: '样式定制',
          desc: '支持 CSS 变量主题定制，完美适配项目设计系统',
          icon: 'el-icon-brush',
          bg: 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
          intro: '样式定制：通过 CSS 变量覆盖默认主题，适配项目的深色/浅色风格'
        }
      ],
      // 表单数据
      formData: {
        username: '',
        email: '',
        role: '',
        bio: ''
      },
      // 表格数据
      tableData: [
        { id: 1, name: '用户管理模块', status: '启用', date: '2024-01-15', desc: '管理系统用户和权限' },
        { id: 2, name: '数据看板', status: '启用', date: '2024-01-20', desc: '实时数据可视化展示' },
        { id: 3, name: '消息中心', status: '禁用', date: '2024-02-01', desc: '系统通知和消息推送' },
        { id: 4, name: '日志审计', status: '启用', date: '2024-02-10', desc: '操作日志和安全审计' }
      ],
      // 步骤进度
      stepActive: 1,
      // 配置说明
      configs: [
        { name: 'nextLabel', type: 'String', desc: '下一步按钮文字' },
        { name: 'prevLabel', type: 'String', desc: '上一步按钮文字' },
        { name: 'skipLabel', type: 'String', desc: '跳过按钮文字' },
        { name: 'doneLabel', type: 'String', desc: '完成按钮文字' },
        { name: 'exitOnOverlayClick', type: 'Boolean', desc: '点击遮罩是否退出（默认 true）' },
        { name: 'exitOnEsc', type: 'Boolean', desc: '按 ESC 是否退出（默认 true）' },
        { name: 'showStepNumbers', type: 'Boolean', desc: '显示步骤序号（默认 false）' },
        { name: 'showBullets', type: 'Boolean', desc: '显示步骤圆点（默认 true）' },
        { name: 'scrollToElement', type: 'Boolean', desc: '自动滚动到目标元素（默认 true）' },
        { name: 'disableInteraction', type: 'Boolean', desc: '禁用高亮区域交互（默认 false）' },
        { name: 'tooltipPosition', type: 'String', desc: 'tooltip 默认位置：top/bottom/left/right/auto' },
        { name: 'scrollPadding', type: 'Number', desc: '滚动到元素时的内边距（默认 30）' }
      ],
      // 使用代码
      usageCode: `<!-- 在页面中使用 SpotlightGuide -->
<template>
  <div>
    <h1 data-step="1" data-intro="这是第一步：页面标题">标题</h1>
    <button data-step="2" data-intro="这是第二步：操作按钮">按钮</button>
    
    <SpotlightGuide ref="spotlight" />
  </div>
</template>

<script>
import SpotlightGuide from '@/components/SpotlightGuide.vue'

export default {
  components: { SpotlightGuide },
  methods: {
    startGuide() {
      this.$refs.spotlight.start([
        { element: '[data-step="1"]', intro: '这是第一步：页面标题', position: 'bottom' },
        { element: '[data-step="2"]', intro: '这是第二步：操作按钮', position: 'right' }
      ], {
        exitOnOverlayClick: false,
        onComplete: () => console.log('引导完成'),
        onExit: () => console.log('引导退出')
      })
    }
  }
}
<\/script>`,
      // Toast 提示
      toastVisible: false,
      toastMessage: ''
    }
  },
  methods: {
    // ========== 完整引导 ==========
    startFullGuide() {
      this.$refs.spotlight.start([
        { element: '[data-step="1"]', intro: '这是页面标题区域，展示当前模块的名称和简介。', position: 'bottom' },
        { element: '[data-step="2"]', intro: '这里展示了聚光高亮的核心能力，每个卡片代表一个功能特性。', position: 'bottom' },
        { element: '.feature-card[data-step="3"]', intro: '聚光高亮：通过超大 box-shadow 实现遮罩压暗，目标区域透明高亮', position: 'bottom' },
        { element: '.feature-card[data-step="4"]', intro: '步骤导航：支持按钮切换、键盘方向键、ESC 退出等多种交互方式', position: 'bottom' },
        { element: '.feature-card[data-step="5"]', intro: '位置自适应：tooltip 气泡自动选择最佳显示位置，避免溢出视口', position: 'bottom' },
        { element: '.feature-card[data-step="6"]', intro: '防误触：通过 exitOnOverlayClick 配置控制遮罩外点击行为', position: 'top' },
        { element: '.feature-card[data-step="7"]', intro: '事件回调：丰富的生命周期钩子，可对接业务逻辑和埋点统计', position: 'top' },
        { element: '.feature-card[data-step="8"]', intro: '样式定制：通过 CSS 变量覆盖默认主题，适配项目的深色/浅色风格', position: 'top' },
        { element: '#scenario-form', intro: '场景 1：表单填写引导 — 逐步引导用户完成表单填写', position: 'bottom' },
        { element: '#scenario-table', intro: '场景 2：数据列表操作引导 — 介绍表格的各项功能', position: 'bottom' },
        { element: '#scenario-steps', intro: '场景 3：步骤流程引导 — 展示多步骤操作流程', position: 'bottom' },
        { element: '#config-section', intro: '这里列出了所有可用的配置选项和参数说明', position: 'top' },
        { element: '#code-section', intro: '完整的使用示例代码，可以直接复制到项目中使用', position: 'top' }
      ], {
        exitOnOverlayClick: false,
        showProgress: true,
        onComplete: () => {
          this.showToast('恭喜！您已完成全部引导步骤')
        },
        onExit: () => {
          this.showToast('引导已退出，可随时重新开始')
        }
      })
    },

    // ========== 表单引导 ==========
    startFormGuide() {
      this.$refs.spotlight.start([
        { element: '.el-form-item:nth-child(1)', intro: '第一步：请输入您的用户名（3-20 个字符）', position: 'right' },
        { element: '.el-form-item:nth-child(2)', intro: '第二步：请输入有效的邮箱地址', position: 'right' },
        { element: '.el-form-item:nth-child(3)', intro: '第三步：选择您的角色类型（管理员/编辑/访客）', position: 'right' },
        { element: '.el-form-item:nth-child(4)', intro: '第四步：简单介绍一下自己（可选）', position: 'right' },
        { element: '.el-form-item:nth-child(5)', intro: '最后：点击提交按钮完成注册', position: 'top' }
      ], {
        exitOnOverlayClick: false,
        onComplete: () => this.showToast('表单引导完成！')
      })
    },

    // ========== 表格引导 ==========
    startTableGuide() {
      this.$refs.spotlight.start([
        { element: '.el-table__header-wrapper', intro: '表格头部：点击列标题可进行排序', position: 'bottom' },
        { element: '.el-table__body-wrapper', intro: '表格主体：展示数据列表，支持行内操作', position: 'bottom' },
        { element: '.el-table__body-wrapper .el-button--primary', intro: '编辑按钮：点击修改当前行数据', position: 'left' },
        { element: '.el-table__body-wrapper .el-button--danger', intro: '删除按钮：点击删除当前行（需二次确认）', position: 'left' }
      ], {
        exitOnOverlayClick: false,
        onComplete: () => this.showToast('表格引导完成！')
      })
    },

    // ========== 步骤引导 ==========
    startStepsGuide() {
      this.$refs.spotlight.start([
        { element: '.el-steps', intro: '步骤条：展示当前流程的进度和状态', position: 'bottom' },
        { element: '.step-actions', intro: '操作区：点击「下一步」推进流程', position: 'top' }
      ], {
        exitOnOverlayClick: false,
        onComplete: () => this.showToast('步骤引导完成！')
      })
    },

    // ========== 重置 ==========
    resetGuide() {
      this.$refs.spotlight.destroy()
      this.stepActive = 1
      this.showToast('已重置，可以重新开始引导')
    },

    // ========== 复制代码 ==========
    copyCode(code) {
      navigator.clipboard.writeText(code).then(() => {
        this.showToast('代码已复制到剪贴板')
      }).catch(() => {
        this.showToast('复制失败，请手动复制')
      })
    },

    // ========== Toast 提示 ==========
    showToast(message) {
      this.toastMessage = message
      this.toastVisible = true
      setTimeout(() => {
        this.toastVisible = false
      }, 2500)
    }
  }
}
</script>

<style scoped>
/* ========== 页面整体 ========== */
.spotlight-demo-page {
  /* 撑满内容区，滚动下沉到模块内部 */
  height: 100%;
  box-sizing: border-box;
  overflow: auto;
  padding: 0;
  background: transparent;
}

/* ========== 页面头部 ========== */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: var(--border-radius-lg, 12px);
  padding: 32px 40px;
  margin-bottom: 32px;
  color: #fff;
  box-shadow: var(--shadow-lg, 0 8px 24px rgba(0, 0, 0, 0.15));
}

.header-content .page-title {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-content .page-title i {
  font-size: 32px;
}

.page-subtitle {
  margin: 8px 0 0;
  font-size: 14px;
  opacity: 0.85;
}

.header-actions {
  display: flex;
  gap: 12px;
}

/* ========== 区块标题 ========== */
.section-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text-primary, #303133);
  margin: 0 0 20px;
  padding-left: 12px;
  border-left: 4px solid #667eea;
}

/* ========== 功能卡片网格 ========== */
.feature-section {
  margin-bottom: 32px;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.feature-card {
  background: var(--bg-color-white, #fff);
  border-radius: var(--border-radius-md, 8px);
  padding: 24px;
  text-align: center;
  transition: all 0.3s ease;
  box-shadow: var(--shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.12));
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg, 0 8px 24px rgba(0, 0, 0, 0.15));
}

.feature-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: #fff;
  font-size: 26px;
}

.feature-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary, #303133);
  margin: 0 0 8px;
}

.feature-desc {
  font-size: 13px;
  color: var(--color-text-secondary, #909399);
  line-height: 1.6;
  margin: 0;
}

/* ========== 演示场景 ========== */
.scenario-card {
  background: var(--bg-color-white, #fff);
  border-radius: var(--border-radius-md, 8px);
  margin-bottom: 20px;
  box-shadow: var(--shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.12));
  overflow: hidden;
}

.scenario-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: var(--bg-color-info, #f4f4f5);
  border-bottom: 1px solid var(--border-color-light, #e4e7ed);
}

.scenario-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary, #303133);
}

.scenario-content {
  padding: 24px;
}

/* ========== 步骤操作 ========== */
.step-actions {
  margin-top: 24px;
  text-align: center;
}

/* ========== 配置说明 ========== */
.config-section {
  margin-bottom: 32px;
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.config-item {
  background: var(--bg-color-white, #fff);
  border-radius: var(--border-radius-base, 4px);
  padding: 16px;
  border-left: 3px solid #67C23A;
  box-shadow: var(--shadow-sm, 0 1px 3px rgba(0, 0, 0, 0.12));
}

.config-name {
  font-size: 14px;
  font-weight: 600;
  color: #667eea;
  background: #ecf5ff;
  padding: 2px 8px;
  border-radius: 4px;
}

.config-type {
  font-size: 12px;
  color: var(--color-text-secondary, #909399);
  margin-left: 8px;
  font-family: 'Consolas', monospace;
}

.config-desc {
  margin: 8px 0 0;
  font-size: 13px;
  color: var(--color-text-regular, #606266);
  line-height: 1.5;
}

/* ========== 代码示例 ========== */
.code-section {
  margin-bottom: 32px;
}

.code-card {
  background: #1e1e1e;
  border-radius: var(--border-radius-md, 8px);
  overflow: hidden;
}

.code-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  background: #2d2d2d;
  border-bottom: 1px solid #3a3a3a;
}

.code-label {
  font-size: 13px;
  color: #abb2bf;
  font-family: 'Consolas', monospace;
}

.code-block {
  margin: 0;
  padding: 20px;
  color: #d4d4d4;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 400px;
  overflow-y: auto;
}

/* ========== Toast 提示 ========== */
.guide-toast {
  position: fixed;
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  z-index: 10000;
  display: flex;
  align-items: center;
  gap: 8px;
  animation: toastSlide 0.3s ease;
}

.guide-toast i {
  font-size: 18px;
  color: #67C23A;
}

@keyframes toastSlide {
  from { opacity: 0; transform: translateX(-50%) translateY(-20px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}

/* ========== 响应式 ========== */
@media (max-width: 1024px) {
  .feature-grid { grid-template-columns: repeat(2, 1fr); }
  .config-grid { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 768px) {
  .page-header { flex-direction: column; text-align: center; gap: 16px; }
  .feature-grid { grid-template-columns: 1fr; }
  .config-grid { grid-template-columns: 1fr; }
}
</style>
